/**
 * AP-INV-01: Invoice Detail + Status History
 * Route: /portal/invoices/:invoiceId (portal-protected)
 *
 * Shows full invoice: header, lines, tax, documents, audit trail.
 * Actions: Submit (DRAFT), Void (DRAFT), Document upload.
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, ChevronRight, Send, Trash2, Paperclip,
  CheckCircle2, XCircle, Clock, Edit3, AlertTriangle,
  Archive, FileText, ExternalLink, UploadCloud, Info,
} from "lucide-react";
import { usePortalUser } from "@/components/PortalProtectedRoute";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CFG = {
  DRAFT:        { label: "Draft",        bg: "#F8F9FA", text: "#495057", border: "#DEE2E6", Icon: Edit3 },
  SUBMITTED:    { label: "Submitted",    bg: "#EFF6FF", text: "#1D4ED8", border: "#BFDBFE", Icon: Send },
  UNDER_REVIEW: { label: "Under Review", bg: "#FEF3C7", text: "#92400E", border: "#FDE68A", Icon: Clock },
  APPROVED:     { label: "Approved",     bg: "#F0FDF4", text: "#166534", border: "#BBF7D0", Icon: CheckCircle2 },
  REJECTED:     { label: "Rejected",     bg: "#FEF2F2", text: "#991B1B", border: "#FECACA", Icon: XCircle },
  DISPUTED:     { label: "Disputed",     bg: "#FFF7ED", text: "#9A3412", border: "#FED7AA", Icon: AlertTriangle },
  VOIDED:       { label: "Voided",       bg: "#F5F5F5", text: "#9CA3AF", border: "#E5E7EB", Icon: Archive },
};

const EVENT_LABELS = {
  DRAFT_CREATED:      { label: "Draft Created",       color: "#6B5B4A" },
  DRAFT_UPDATED:      { label: "Draft Updated",        color: "#6B5B4A" },
  SUBMITTED:          { label: "Invoice Submitted",    color: "#1D4ED8" },
  UNDER_REVIEW:       { label: "Under Review",         color: "#92400E" },
  APPROVED:           { label: "Approved",             color: "#166534" },
  REJECTED:           { label: "Invoice Rejected",     color: "#991B1B" },
  VOIDED:             { label: "Invoice Voided",       color: "#9CA3AF" },
  DOCUMENT_ATTACHED:  { label: "Document Attached",    color: "#6B5B4A" },
};

function fmt(minor, currency = "INR") {
  if (minor == null) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency", currency,
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(minor / 100);
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function fmtQty(qty) {
  if (qty === "" || qty == null) return "—";
  const n = parseFloat(qty);
  return isNaN(n) ? qty : n % 1 === 0 ? String(Math.round(n)) : n.toFixed(3).replace(/\.?0+$/, "");
}

function StatusBadge({ status, large = false }) {
  const cfg = STATUS_CFG[status] || { label: status, bg: "#F5F5F5", text: "#666", border: "#DDD", Icon: FileText };
  const Ic = cfg.Icon;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: "5px",
      padding: large ? "6px 14px" : "3px 8px",
      borderRadius: "20px", fontSize: large ? "14px" : "11px", fontWeight: 700,
      background: cfg.bg, color: cfg.text, border: `1px solid ${cfg.border}`,
    }}>
      <Ic size={large ? 15 : 11} />
      {cfg.label}
    </span>
  );
}

export default function SupplierInvoiceDetail() {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [invoice,      setInvoice]      = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState("");
  const [actionBusy,   setActionBusy]   = useState(false);
  const [showVoidConfirm, setShowVoidConfirm] = useState(false);
  const [docNote,      setDocNote]      = useState("");
  const [payStatus,    setPayStatus]    = useState(null);  // AP-PAY-01D

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/portal/invoices/${invoiceId}`, { withCredentials: true });
      setInvoice(res.data);
      // AP-PAY-01D: load payment status for APPROVED invoices
      if (res.data?.status === "APPROVED") {
        try {
          const ps = await axios.get(`${API}/api/portal/payments/invoice/${invoiceId}`, { withCredentials: true });
          setPayStatus(ps.data);
        } catch { /* payment status non-blocking */ }
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load invoice");
    } finally {
      setLoading(false);
    }
  }, [invoiceId]);

  useEffect(() => { load(); }, [load]);

  async function handleSubmit() {
    if (!invoice) return;
    setActionBusy(true);
    try {
      await axios.post(`${API}/api/portal/invoices/${invoiceId}/submit`, {}, { withCredentials: true });
      toast.success("Invoice submitted to AP team");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed");
    } finally {
      setActionBusy(false);
    }
  }

  async function handleVoid() {
    setActionBusy(true);
    try {
      await axios.delete(`${API}/api/portal/invoices/${invoiceId}`, { withCredentials: true });
      toast.success("Invoice voided");
      navigate("/portal/invoices");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Void failed");
    } finally {
      setActionBusy(false);
      setShowVoidConfirm(false);
    }
  }

  async function handleFileUpload(file) {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("document_type", "INVOICE");
    if (docNote) formData.append("note", docNote);
    setActionBusy(true);
    try {
      await axios.post(`${API}/api/portal/invoices/${invoiceId}/documents`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Document attached");
      setDocNote("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setActionBusy(false);
    }
  }

  if (loading) return (
    <div style={{ minHeight: "100vh", background: "#FBF7F2", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans', sans-serif" }}>
      <p style={{ color: "#9CA3AF", fontSize: "14px" }}>Loading invoice…</p>
    </div>
  );

  if (error || !invoice) return (
    <div style={{ minHeight: "100vh", background: "#FBF7F2", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans', sans-serif" }}>
      <div style={{ textAlign: "center" }}>
        <AlertTriangle size={40} style={{ color: "#DC2626", marginBottom: "16px" }} />
        <p style={{ fontSize: "15px", color: "#DC2626" }}>{error || "Invoice not found"}</p>
        <button onClick={() => navigate("/portal/invoices")}
          style={{ marginTop: "16px", padding: "8px 18px", background: "#1A1A1A", color: "#C9A15F", border: "1px solid #C9A15F", borderRadius: "6px", cursor: "pointer", fontSize: "13px" }}>
          Back to Invoices
        </button>
      </div>
    </div>
  );

  const isDraft = invoice.status === "DRAFT";
  const isSubmittable = isDraft;
  const currency = invoice.currency_code || "INR";

  return (
    <div data-testid="supplier-invoice-detail-page" style={{ minHeight: "100vh", background: "#FBF7F2", fontFamily: "'DM Sans', sans-serif" }}>
      {/* Nav */}
      <nav style={{ background: "#1A1A1A", borderBottom: "1px solid rgba(201,161,95,0.2)", padding: "0 24px", height: "60px", display: "flex", alignItems: "center", gap: "16px", position: "sticky", top: 0, zIndex: 10 }}>
        <button onClick={() => navigate("/portal/invoices")} data-testid="back-to-invoices-btn"
          style={{ display: "flex", alignItems: "center", gap: "6px", background: "none", border: "none", color: "#8A7A6A", cursor: "pointer", fontSize: "13px" }}>
          <ArrowLeft size={16} /> My Invoices
        </button>
        <ChevronRight size={14} style={{ color: "#555" }} />
        <span style={{ fontSize: "13px", color: "#C9A15F", fontFamily: "monospace" }}>
          {invoice.ap_reference || "Draft Invoice"}
        </span>
      </nav>

      <div style={{ maxWidth: "1000px", margin: "0 auto", padding: "32px 24px" }}>
        {/* Header Card */}
        <div data-testid="invoice-header-card" style={{ background: "#1A1A1A", border: "1px solid #2D2D2D", borderRadius: "12px", padding: "28px", marginBottom: "20px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "16px", flexWrap: "wrap" }}>
            <div>
              <p style={{ fontSize: "11px", color: "#8A7A6A", margin: "0 0 6px", textTransform: "uppercase", letterSpacing: "1px" }}>
                {invoice.ap_reference ? "AP Reference" : "Draft Invoice"}
              </p>
              <h2 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: "26px", fontWeight: 600, color: "#F5EFE6", margin: "0 0 4px" }}>
                {invoice.ap_reference || invoice.supplier_invoice_number || "Unsaved Draft"}
              </h2>
              {invoice.ap_reference && (
                <p style={{ fontSize: "13px", color: "#8A7A6A", margin: "0 0 16px" }}>
                  Supplier Invoice: <strong style={{ color: "#C9A15F" }}>{invoice.supplier_invoice_number}</strong>
                </p>
              )}
              <StatusBadge status={invoice.status} large />
            </div>
            <div style={{ textAlign: "right" }}>
              <p style={{ fontSize: "11px", color: "#8A7A6A", margin: "0 0 4px", textTransform: "uppercase" }}>Invoice Total</p>
              <p style={{ fontSize: "28px", fontWeight: 700, color: "#C9A15F", margin: 0 }}>
                {fmt(invoice.total_minor, currency)}
              </p>
              <p style={{ fontSize: "11px", color: "#6B5B4A", marginTop: "4px" }}>
                Incl. Tax: {fmt(invoice.tax_total_minor, currency)}
              </p>
            </div>
          </div>

          {/* Meta row */}
          <div style={{ display: "flex", gap: "28px", marginTop: "24px", borderTop: "1px solid rgba(255,255,255,0.07)", paddingTop: "20px", flexWrap: "wrap" }}>
            {[
              { label: "Invoice Date",    value: fmtDate(invoice.invoice_date) },
              { label: "Due Date",        value: fmtDate(invoice.due_date) },
              { label: "Purchase Order",  value: invoice.po_number || "—" },
              { label: "Submitted At",    value: invoice.submitted_at ? fmtDateTime(invoice.submitted_at) : "—" },
            ].map(({ label, value }) => (
              <div key={label}>
                <p style={{ fontSize: "10px", color: "#6B5B4A", margin: "0 0 3px", textTransform: "uppercase", letterSpacing: "0.5px" }}>{label}</p>
                <p style={{ fontSize: "13px", color: "#F5EFE6", margin: 0, fontWeight: 500 }}>{value}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Actions */}
        {isDraft && (
          <div style={{ display: "flex", gap: "12px", marginBottom: "20px", flexWrap: "wrap" }}>
            <button data-testid="submit-invoice-btn"
              disabled={actionBusy}
              onClick={handleSubmit}
              style={{ display: "flex", alignItems: "center", gap: "8px", padding: "10px 20px", background: "#C9A15F", border: "none", color: "#1A1A1A", borderRadius: "8px", fontSize: "13px", fontWeight: 700, cursor: actionBusy ? "wait" : "pointer" }}>
              <Send size={15} />
              {actionBusy ? "Submitting…" : "Submit Invoice"}
            </button>
            <button data-testid="void-invoice-btn"
              disabled={actionBusy}
              onClick={() => setShowVoidConfirm(true)}
              style={{ display: "flex", alignItems: "center", gap: "8px", padding: "10px 20px", background: "transparent", border: "1px solid #FECACA", color: "#DC2626", borderRadius: "8px", fontSize: "13px", fontWeight: 600, cursor: actionBusy ? "wait" : "pointer" }}>
              <Trash2 size={15} />
              Void Draft
            </button>
          </div>
        )}

        {/* Invoice Lines */}
        <div style={{ background: "#FFF", border: "1px solid #EDE8E0", borderRadius: "12px", overflow: "hidden", marginBottom: "20px" }}>
          <div style={{ padding: "16px 20px", borderBottom: "1px solid #EDE8E0" }}>
            <h3 style={{ fontSize: "13px", fontWeight: 700, color: "#1A1A1A", margin: 0, textTransform: "uppercase", letterSpacing: "0.5px" }}>Invoice Lines</h3>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#F9F5F0" }}>
                  {["Description", "Unit", "Qty", "Unit Rate", "Subtotal", "Tax", "Line Total"].map((h, i) => (
                    <th key={i} style={{ padding: "10px 16px", textAlign: i >= 2 ? "right" : "left", fontSize: "11px", fontWeight: 700, color: "#6B5B4A", letterSpacing: "0.4px", textTransform: "uppercase", borderBottom: "1px solid #EDE8E0", whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(invoice.lines || []).map((line, idx) => (
                  <tr key={line.po_line_id || idx} style={{ borderBottom: "1px solid #F5F0EB" }}>
                    <td style={{ padding: "12px 16px", fontSize: "13px", color: "#1A1A1A" }}>{line.description || "—"}</td>
                    <td style={{ padding: "12px 16px", fontSize: "12px", color: "#6B5B4A" }}>{line.unit || "—"}</td>
                    <td style={{ padding: "12px 16px", textAlign: "right", fontSize: "13px", fontWeight: 600 }}>{fmtQty(line.invoiced_quantity)}</td>
                    <td style={{ padding: "12px 16px", textAlign: "right", fontSize: "12px", color: "#6B5B4A" }}>{fmt(line.unit_rate_minor, currency)}</td>
                    <td style={{ padding: "12px 16px", textAlign: "right", fontSize: "12px" }}>{fmt(line.subtotal_minor, currency)}</td>
                    <td style={{ padding: "12px 16px", textAlign: "right", fontSize: "12px", color: "#6B5B4A" }}>
                      {fmt(line.tax_amount_minor, currency)}
                      {line.tax_rate_bps > 0 && (
                        <span style={{ fontSize: "10px", color: "#9CA3AF", marginLeft: "4px" }}>
                          ({line.tax_rate_bps / 100}%)
                        </span>
                      )}
                    </td>
                    <td style={{ padding: "12px 16px", textAlign: "right", fontWeight: 600, fontSize: "13px" }}>{fmt(line.line_total_minor, currency)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ background: "#F9F5F0", borderTop: "2px solid #EDE8E0" }}>
                  <td colSpan={4} />
                  <td style={{ padding: "12px 16px", textAlign: "right", fontSize: "12px", fontWeight: 600, color: "#6B5B4A" }}>
                    Subtotal: {fmt(invoice.subtotal_minor, currency)}
                  </td>
                  <td style={{ padding: "12px 16px", textAlign: "right", fontSize: "12px", fontWeight: 600, color: "#6B5B4A" }}>
                    Tax: {fmt(invoice.tax_total_minor, currency)}
                  </td>
                  <td style={{ padding: "12px 16px", textAlign: "right", fontSize: "14px", fontWeight: 700, color: "#1A1A1A" }}>
                    {fmt(invoice.total_minor, currency)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {/* Documents */}
        <div style={{ background: "#FFF", border: "1px solid #EDE8E0", borderRadius: "12px", overflow: "hidden", marginBottom: "20px" }}>
          <div style={{ padding: "16px 20px", borderBottom: "1px solid #EDE8E0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <h3 style={{ fontSize: "13px", fontWeight: 700, color: "#1A1A1A", margin: 0, textTransform: "uppercase", letterSpacing: "0.5px" }}>
              Supporting Documents
            </h3>
            {invoice.status !== "VOIDED" && (
              <button data-testid="attach-document-btn"
                onClick={() => fileInputRef.current?.click()}
                style={{ display: "flex", alignItems: "center", gap: "6px", padding: "6px 12px", background: "transparent", border: "1px solid #DDD6CE", borderRadius: "6px", fontSize: "12px", color: "#6B5B4A", cursor: "pointer" }}>
                <UploadCloud size={13} /> Attach
              </button>
            )}
          </div>
          <input ref={fileInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.heic" style={{ display: "none" }}
            onChange={(e) => { if (e.target.files[0]) handleFileUpload(e.target.files[0]); }} />

          {(invoice.documents || []).length === 0 ? (
            <div style={{ padding: "32px", textAlign: "center" }}>
              <Paperclip size={28} style={{ color: "#DDD6CE", marginBottom: "12px" }} />
              <p style={{ fontSize: "13px", color: "#9CA3AF", margin: 0 }}>No documents attached yet</p>
            </div>
          ) : (
            <div style={{ padding: "16px 20px" }}>
              {(invoice.documents || []).map((doc, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: "12px", padding: "10px 0", borderBottom: i < invoice.documents.length - 1 ? "1px solid #F5F0EB" : "none" }}>
                  <FileText size={16} style={{ color: "#C9A15F", flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: "13px", color: "#1A1A1A", margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{doc.file_name || "Document"}</p>
                    <p style={{ fontSize: "11px", color: "#9CA3AF", margin: "2px 0 0" }}>{doc.document_type} · {fmtDateTime(doc.uploaded_at)}</p>
                  </div>
                  {doc.url && (
                    <a href={doc.url} target="_blank" rel="noreferrer"
                      style={{ color: "#C9A15F", display: "flex", alignItems: "center", gap: "4px", fontSize: "12px" }}>
                      <ExternalLink size={13} /> View
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Audit History */}
        <div style={{ background: "#FFF", border: "1px solid #EDE8E0", borderRadius: "12px", overflow: "hidden" }}>
          <div style={{ padding: "16px 20px", borderBottom: "1px solid #EDE8E0" }}>
            <h3 style={{ fontSize: "13px", fontWeight: 700, color: "#1A1A1A", margin: 0, textTransform: "uppercase", letterSpacing: "0.5px" }}>Status History</h3>
          </div>
          <div style={{ padding: "20px" }}>
            {(invoice.audit_events || []).length === 0 ? (
              <p style={{ fontSize: "13px", color: "#9CA3AF", textAlign: "center", margin: 0 }}>No history</p>
            ) : (
              <div style={{ position: "relative" }}>
                {/* Timeline line */}
                <div style={{ position: "absolute", left: "13px", top: "8px", bottom: "8px", width: "2px", background: "#EDE8E0" }} />
                {[...(invoice.audit_events || [])].reverse().map((ev, i) => {
                  const cfg = EVENT_LABELS[ev.event_type] || { label: ev.event_type, color: "#6B5B4A" };
                  return (
                    <div key={i} data-testid={`audit-event-${i}`} style={{ display: "flex", gap: "16px", marginBottom: "20px", position: "relative" }}>
                      <div style={{ width: "28px", height: "28px", borderRadius: "50%", background: "#FFF", border: `2px solid ${cfg.color}`, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", position: "relative", zIndex: 1 }}>
                        <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: cfg.color }} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "8px", flexWrap: "wrap" }}>
                          <span style={{ fontSize: "13px", fontWeight: 600, color: cfg.color }}>{cfg.label}</span>
                          <span style={{ fontSize: "11px", color: "#9CA3AF", whiteSpace: "nowrap" }}>{fmtDateTime(ev.timestamp)}</span>
                        </div>
                        {ev.comment && <p style={{ fontSize: "12px", color: "#6B5B4A", margin: "4px 0 0" }}>{ev.comment}</p>}
                        {ev.triggered_by && <p style={{ fontSize: "11px", color: "#9CA3AF", margin: "2px 0 0" }}>by {ev.triggered_by}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

        {/* ── AP-PAY-01D: Payment Status (APPROVED invoices only) ──────────── */}
        {invoice.status === "APPROVED" && payStatus && (
          <div data-testid="portal-payment-status" style={{
            background: "#FFF", borderRadius: "12px", margin: "0 24px 24px",
            border: "1px solid #E7D8C0", padding: "24px",
          }}>
            <h3 style={{ margin: "0 0 16px", fontSize: "14px", fontWeight: 700, color: "#1A1A1A" }}>
              Payment Status
            </h3>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px,1fr))", gap: "16px" }}>
              {[
                { label: "Invoice Total",   value: fmt(payStatus.invoice_total_minor,    payStatus.currency_code) },
                { label: "Amount Paid",     value: fmt(payStatus.amount_paid_minor,       payStatus.currency_code) },
                { label: "Credit Applied",  value: fmt(payStatus.credit_note_total_minor, payStatus.currency_code) },
                { label: "Outstanding",     value: fmt(payStatus.outstanding_amount_minor, payStatus.currency_code) },
              ].map(({ label, value }) => (
                <div key={label}>
                  <div style={{ fontSize: "11px", color: "#9CA3AF", fontWeight: 600, marginBottom: "3px" }}>{label}</div>
                  <div style={{ fontSize: "15px", fontWeight: 700, color: "#1A1A1A" }}>{value}</div>
                </div>
              ))}
            </div>
            {payStatus.is_fully_paid && (
              <div style={{ marginTop: "14px", padding: "10px 14px", background: "#F0FDF4",
                border: "1px solid #BBF7D0", borderRadius: "8px", fontSize: "13px",
                color: "#166534", fontWeight: 600 }}>
                Invoice is fully settled
              </div>
            )}
            {payStatus.payment_references?.length > 0 && (
              <div style={{ marginTop: "12px", fontSize: "12px", color: "#9CA3AF" }}>
                {payStatus.payment_references.length} payment reference{payStatus.payment_references.length !== 1 ? "s" : ""} applied
              </div>
            )}
          </div>
        )}
      {showVoidConfirm && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}>
          <div data-testid="void-confirm-modal" style={{ background: "#FFF", borderRadius: "12px", padding: "28px", maxWidth: "400px", width: "100%", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
            <div style={{ display: "flex", gap: "12px", marginBottom: "16px", alignItems: "flex-start" }}>
              <Trash2 size={20} style={{ color: "#DC2626", flexShrink: 0, marginTop: "2px" }} />
              <div>
                <h3 style={{ fontSize: "15px", fontWeight: 700, color: "#1A1A1A", margin: "0 0 8px" }}>Void this draft?</h3>
                <p style={{ fontSize: "13px", color: "#6B5B4A", margin: 0, lineHeight: 1.5 }}>
                  This will void the draft and release all quantity reservations. This action cannot be undone.
                </p>
              </div>
            </div>
            <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
              <button onClick={() => setShowVoidConfirm(false)}
                style={{ padding: "8px 16px", background: "transparent", border: "1px solid #DDD", borderRadius: "6px", cursor: "pointer", fontSize: "13px" }}>
                Cancel
              </button>
              <button data-testid="confirm-void-btn" onClick={handleVoid} disabled={actionBusy}
                style={{ padding: "8px 16px", background: "#DC2626", border: "none", color: "#FFF", borderRadius: "6px", cursor: "pointer", fontSize: "13px", fontWeight: 600 }}>
                {actionBusy ? "Voiding…" : "Void Invoice"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
